import { Row, Col, Modal } from 'antd';
import { useLocation } from 'react-router-dom';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

import '../css/Advantages.css';
import HeroSection from '../components/HeroSection';
import BlockText from '../components/BlockText';
import DataStatisticsCard from '../components/DataStatisticsCard';
import CertificateCarousel from '../components/CertificateCarousel';

function Advantages(){
    
    // Production section data
    const productionData = [
        { titleCh: "製作廠區", titleEn: "Manufacturing Site", value: "2", unitCh: "間", unitEn: "sites" },
        { titleCh: "生產線", titleEn: "Production Lines", value: "15+", unitCh: "條", unitEn: "lines" }
    ];

    // Product Journey section
    const [currentStep, setCurrentStep] = useState(0);
    const flowData = [
        {
            id: 0,
            titleCh: '原物料入庫',
            titleEn: 'Material Storage',
            img: 'flow_1.jpg',
            btnIcon: 'Icon_1.svg'
        },
        {
            id: 1,
            titleCh: '添加物秤料',
            titleEn: 'Scaling',
            img: 'flow_2.jpg',
            btnIcon: 'Icon_2.svg'
        },
        {
            id: 2,
            titleCh: '調理作業',
            titleEn: 'Processing',
            img: 'flow_3.jpg',
            btnIcon: 'Icon_3.svg'
        },
        {
            id: 3,
            titleCh: '品質控管',
            titleEn: 'Quality Control',
            img: 'flow_4.jpg',
            btnIcon: 'Icon_4.svg'
        },
        {
            id: 4,
            titleCh: '產品入庫',
            titleEn: 'Goods Storage',
            img: 'flow_5.jpg',
            btnIcon: 'Icon_5.svg'
        },
        {
            id: 5,
            titleCh: '出貨',
            titleEn: 'Shipping',
            img: 'flow_6.jpg',
            btnIcon: 'Icon_6.svg'
        }
    ];

    const siteUrl = 'https://www.tangwell.com.tw';
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        url: `${siteUrl}/advantages`,
        name: '製造能力與食安優勢',
        description: '湯武生技整合雙廠產線與國際認證，打造穩定供應與高品質餐飲原料解決方案。',
        publisher: {
            '@type': 'Organization',
            name: '湯武生技',
            url: siteUrl
        }
    };

    // 沿用與 Header 相同的判斷邏輯 (中英文切換)
    const location = useLocation();
    const isEnglish = location.pathname.startsWith('/en');
    
    return(
        <div>
            <Helmet>
                <title>製造能力與食安優勢</title>
                <meta name="description" content="湯武生技整合雙廠產線與國際認證，打造穩定供應與高品質餐飲原料解決方案。" />
                <meta name="keywords" content="食品安全, 國際認證" />
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            </Helmet>
            <HeroSection 
                titleEn="Manufacturing Excellence"
                titleCh="卓越製造實力"
                bgImageName="advantage-hero.jpg"
                contentCh="整合雙廠專業與國際規範，湯武生技將生產效能轉化為客戶優勢，打造全球風味基地。"
                contentEn="Integrating global standards and dual-plant expertise, Tang Wu transforms production efficiency into your market advantage."
            />

            {/*Taiwan factory sections */ }
            <section>
                <div className="container-narrow narrow-title-box">
                    <div className="title-en">Production Sites & Facilities</div>
                    <div className="title-ch">生產據點與規模</div>
                </div>
                <div className='container-narrow production-section'>
                    <Row gutter={[16, 16]} className="production-facility-row">
                        <Col xs={24} md={16} className="production-list">
                            {/* 左側清單 - 中英文 */}
                            {isEnglish ? (
                                <>
                                    <div className="content-en">
                                        <ul>
                                            <li>Factory 1: Natural Juice Cold Storage & Flavor Powder Production</li>
                                            <li>Factory 2: Jam Syrup Series, Jam Series For Ice, Coconut Jelly Syrup Series, Jelly Syrup Series, and Automated Filling & Packaging Lines</li>
                                        </ul>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="content-ch">
                                        <ul>
                                            <li>一廠為天然果汁專業冷凍庫、調味粉生產區</li>
                                            <li>二廠為調味糖漿、果醬調味糖漿、椰果調味糖漿、Q果調味糖漿、自動充填包裝生產區。</li>
                                        </ul>
                                    </div>
                                </>
                            )}
                            
                            
                        </Col>
                        <Col xs={24} md={8} className="facility-box">
                            {/* 右側地圖區 */}
                            <div className="map-background-taiwan">
                                <img src={require('../images/taiwan-map.png')} alt="工廠位置圖" className="map-img-tw" loading="lazy" />
                                {/* 圓形標記標籤 - 中英文 */}
                                <div className="map-location-tag">
                                    {isEnglish ? (
                                        <>
                                            <div className="content-en">
                                                Quanxing <br />Industrial Park<br />Changhua County Taiwan
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="content-ch">
                                                台灣彰化縣<br />伸港鄉全興<br />工業區
                                            </div>
                                        </>
                                    )}
                                    
                                    
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <DataStatisticsCard dataList={productionData} />
                </div>
            </section>

            {/*slogan */}
            <div className="slogan-section">
                <div className="big-title-ch">自動化生產線的策略，提升產能35%，降低人力成本25%</div>
                <div className="title-en">Automated production strategy: increased capacity by 35% and reduced labor costs by 25%.</div>
            </div>

            {/* Products Journey Section */}
            <section>
                <div className="container-wide">
                    <BlockText
                        titleEn="The Journey of Our Products"
                        titleCh="生產履歷流程"
                        contentCh="我們嚴格把關生產流程，確保每項產品符合最高品質標準。"
                        contentEn="We strictly monitor production to ensure every product meets the highest quality standards."
                    />
                </div>
                
                {/* 上方展示區：僅顯示圖片 */}
                <div className="flow-display-area">
                    <div className="display-image-box">
                        <img
                            src={require(`../images/ProductJourneySection/${flowData[currentStep].img}`)}
                            alt={flowData[currentStep].titleCh}
                            className="fade-in"
                            key={currentStep} /* key 改變會觸發重新渲染動畫 */
                            loading="lazy"
                        />
                    </div>
                </div>

                <div className="container-wide">
                    {/* 下方導覽區：流程按鈕 */}
                    <div className="flow-nav-wrapper">
                        <div className="flow-buttons-row">
                            {flowData.map((item, index) => (
                                <React.Fragment key={item.id}>
                                    <div
                                        className={`flow-nav-btn ${currentStep === index ? 'active' : ''}`}
                                        onClick={() => setCurrentStep(index)}
                                    >
                                        {/* SVG 圖示容器 */}
                                        <div className="btn-svg-circle">
                                            <img
                                                src={require(`../images/ProductJourneySection/${item.btnIcon}`)}
                                                alt="流程圖示"
                                                className="svg-icon"
                                                loading="lazy"
                                            />
                                        </div>

                                        <div className="btn-content">
                                            <div className="content-ch">{item.titleCh}</div>
                                            <div className="content-en">{item.titleEn}</div>
                                        </div>
                                    </div>

                                    {/* 箭頭間隔 */}
                                    {index < flowData.length - 1 && (
                                        <div className="flow-arrow">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <path d="M9 5L16 12L9 19" stroke="var(--dark)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Quality Section */}
            <section>
                <div className="quality-header-bg" style={{ backgroundImage: `url(${require('../images/CertificateSection/Quality_section.jpg')})` }}>
                    <div className="container-narrow">
                        
                        <BlockText
                            titleEn="Quality"
                            titleCh="生產品質"
                        />
                    </div>
                </div>

                {/* 下半部分 */}
                <div className='container-narrow quality-cards-wrapper'>
                    <div className='quality-card'>
                        <BlockText
                            titleEn="Global Food Safety Certification"
                            titleCh="全球食安體系驗證"
                            contentCh={
                                <ul className="certification-list">
                                    <li className="mb-10">
                                        <div className="ch mb-5">國際體系驗證：通過ISO 22000 食品安全管理系統、FSSC 22000（GFSI 承認）食品安全管理系統、HACCP 食品安全管制系統與台灣清真 (Halal) 驗證。</div>
                                    </li>
                                    <li className="mb-10">
                                        <div className="ch mb-5">官方訪廠認可：具備美國 FDA 與韓國 KFDA 實地訪廠認可，符合國際官方標準。</div>
                                    </li>
                                    <li className="mb-10">
                                        <div className="ch mb-5">第三方定期檢驗：產品定期委託第三方檢驗機構（如振泰檢驗），確保檢測結果符合法規要求。</div>
                                    </li>
                                </ul>
                            }
                            contentEn={
                                <ul className="certification-list">
                                    <li className="mb-10">
                                        <div className="content-en">International System Certifications: Certified under ISO 22000, FSSC 22000 (GFSI-recognized), HACCP, and Taiwan Halal certification.</div>
                                    </li>
                                    <li className="mb-10">
                                        <div className="content-en">Official Site Audit Recognition: Successfully passed on-site audits by the U.S. FDA and South Korea's KFDA, demonstrating compliance with international official standards.</div>
                                    </li>
                                    <li className="mb-10">
                                        <div className="content-en">Regular Third-Party Testing: Products are periodically commissioned to third-party testing agencontent-encies (e.g., Zenitesta) to ensure that results meet regulatory requirements.</div>
                                    </li>
                                </ul>
                            }
                        />
                        <CertificateCarousel filterType="system" slidesToShow={3} variant="advantage" />
                    </div>

                    <div className='quality-card'>
                        <BlockText
                            titleEn="International Professional Credentials"
                            titleCh="專業人員國際證照"
                            contentCh="本公司專業團隊已取得 PCQI 與 FSVP 國際證照，確保產品生產與出口程序完全符合美國 FSMA 法規要求。透過執行 FSVP 計畫與專人監管，嚴格把關進口食品之法規遵循。此外，本公司亦具備 ServSafe 食品安全經理證照，深化食安風險之應對與管理能力。"
                            contentEn="Our team holds PCQI and FSVP certifications, ensuring all production and exports fully meet U.S. FSMA regulations. Through our FSVP program and dedicated oversight, we ensure strict legal compliance for food imports. Additionally, we hold ServSafe Food Protection Manager certifications to strengthen our food safety risk management."
                        />
                        <CertificateCarousel filterType="people" slidesToShow={3} variant="advantage" />
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Advantages;